import { ENUM_COKITCHEN_TEAM, ENUM_CS_ROLE } from '@cokitchen/cokitchen-auth';
import { OrderType } from 'models/order.model';
import { STORAGE_KEYS } from 'utils/constants';
import {
  CS_LIVE_CARD_LIST,
  ENUM_LIVE_CARD_MENU,
  KITCHEN_LIVE_CARD_LIST,
  LiveCardListType,
  LOGISTICS_LIVE_CARD_LIST
} from './menu-config';
import { getCurrentTeam } from 'utils/role-config';
import { getCSAdmin, getKitchenOrderStatus, getOrderStatus, isCSRep, pipe } from 'utils/functions';

type FilterListType = { list: LiveCardListType[]; order: OrderType; ridersAvailable: boolean };

const filterDeliveryState = (obj: FilterListType) => {
  const { list, order } = obj;

  const newList = list.filter((item) => {
    if (order?.calculated_order?.pickup) return item.key !== ENUM_LIVE_CARD_MENU.SWITCH_TO_PICKUP;
    return item.key !== ENUM_LIVE_CARD_MENU.SWITCH_TO_DELIVERY;
  });

  return { ...obj, list: newList };
};

const filterRiderState = (obj: FilterListType) => {
  const { list, order } = obj;
  const isRiderAssigned = order.rider_assigned;

  const newList = list.filter((item) => {
    if (isRiderAssigned) {
      return item.key !== ENUM_LIVE_CARD_MENU.ASSIGN_RIDER;
    }
    return (
      item.key !== ENUM_LIVE_CARD_MENU.REASSIGN_RIDER &&
      item.key !== ENUM_LIVE_CARD_MENU.UNASSIGN_RIDER
    );
  });
  return { ...obj, list: newList };
};

const filterPaidState = (obj: FilterListType) => {
  const { list, order } = obj;
  const isCsAdmin = getCurrentTeam() === ENUM_COKITCHEN_TEAM.CS && getCSAdmin();

  const newList =
    order.paid && (!isCsAdmin || order.order_details.use_wallet)
      ? list.filter((i) => i.key !== ENUM_LIVE_CARD_MENU.CONFIRM_PAYMENT)
      : list;

  return { ...obj, list: newList };
};

const filterCsSuperAdminState = (obj: FilterListType) => {
  const { list } = obj;
  const role = localStorage.getItem(STORAGE_KEYS.ROLE);
  const isCsSuperAdmin = role === ENUM_CS_ROLE.CS_SUPER_ADMIN;
  const newList = !isCsSuperAdmin
    ? list.filter(
        (i) =>
          ![
            ENUM_LIVE_CARD_MENU.REASSIGN_REP,
            ENUM_LIVE_CARD_MENU.REASSIGN_RIDER,
            ENUM_LIVE_CARD_MENU.UNASSIGN_RIDER
          ].includes(i.key)
      )
    : list;

  return { ...obj, list: newList };
};

const filterCsRepState = (obj: FilterListType) => {
  const { list } = obj;

  const newList = !isCSRep()
    ? list.filter((i) => i.key !== ENUM_LIVE_CARD_MENU.ESCALATE_ORDER)
    : list;

  return { ...obj, list: newList };
};

const filterShipBubble = (obj: FilterListType) => {
  const { list, ridersAvailable } = obj;

  const newList = ridersAvailable
    ? list.filter((item) => item.key !== ENUM_LIVE_CARD_MENU.REQUEST_DELIVERY_FROM_SHIPBUBBLE)
    : list;

  return { ...obj, list: newList };
};

export const getLiveCardMenuList = (order: OrderType, ridersAvailable: boolean) => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE);
  const currentTeam = getCurrentTeam();

  if (!role || !currentTeam) return [];

  const type =
    currentTeam === ENUM_COKITCHEN_TEAM.KITCHEN
      ? getKitchenOrderStatus(order)
      : getOrderStatus(order);
  const listMapping: Record<ENUM_COKITCHEN_TEAM, LiveCardListType[]> = {
    cs: CS_LIVE_CARD_LIST[type],
    kitchen: KITCHEN_LIVE_CARD_LIST[type],
    logistics: LOGISTICS_LIVE_CARD_LIST[type],
    security: []
  };

  const mappedList = listMapping[currentTeam];

  if (!mappedList) return [];

  const processValue = pipe<FilterListType>([
    filterDeliveryState,
    filterPaidState,
    filterRiderState,
    filterShipBubble,
    filterCsSuperAdminState,
    filterCsRepState
  ]);

  const { list } = processValue({ list: mappedList, order, ridersAvailable });
  return list;
};
